import React, { useState, useEffect, useRef } from 'react';
import { X, Shield, Lock, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import { addNotification } from '../services/notificationService';
import otpCallbackRegistry from '../utils/otpCallbackRegistry';
import { useSettings } from '../contexts/SettingsContext';

const OTPConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  actionType = 'delete', // 'delete', 'remove_cart_item', 'delete_held_sale'
  itemName = '',
  itemDetails = null,
  title = 'Confirm Action',
  description = 'Please enter the OTP to confirm this action.'
}) => {
  const { getSetting } = useSettings();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [generatedOtp, setGeneratedOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(25); // 25 seconds
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 3;
  const inputRefs = useRef([]);

  // Check OTP requirement setting and bypass if disabled
  useEffect(() => {
    if (isOpen) {
      const requireOtp = getSetting('require_otp_for_delete', true);
      
      // If OTP is not required, execute the callback immediately
      if (!requireOtp) {
        console.log('🔐 OTP is disabled in settings, executing callback directly');
        if (typeof onConfirm === 'function') {
          onConfirm();
        }
        onClose();
        return;
      }
      
      console.log('🔐 OTP Modal opened, generating OTP...');
      console.log('🔐 onConfirm function:', typeof onConfirm, onConfirm);
      console.log('🔐 actionType:', actionType);
      console.log('🔐 itemName:', itemName);
      console.log('🔐 itemDetails:', itemDetails);
      generateOTP();
      setOtp(['', '', '', '', '', '']);
      setAttempts(0);
      setTimeLeft(25);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Timer countdown
  useEffect(() => {
    if (isOpen && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, timeLeft]);

  // Auto-focus first OTP input
  useEffect(() => {
    if (isOpen && inputRefs.current[0]) {
      setTimeout(() => inputRefs.current[0].focus(), 100);
    }
  }, [isOpen]);

  const generateOTP = () => {
    const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(newOtp);
    
    console.log('🔐 Generating OTP and registering callback:', newOtp, {
      actionType,
      itemName,
      itemDetails
    });
    
    // Register the callback in the registry (functions can't be serialized)
    if (typeof onConfirm === 'function') {
      console.log('🔐 Registering callback for OTP:', newOtp);
      otpCallbackRegistry.register(newOtp, onConfirm, {
        actionType,
        itemName,
        itemDetails
      });
    } else {
      console.error('🔐 ERROR: onConfirm is not a function:', typeof onConfirm, onConfirm);
    }
    
    // Prepare metadata with OTP and items information
    const metadata = {
      isOTP: true,
      expectedOtp: newOtp,
      actionType,
      items: itemDetails?.items || [], // Items to be affected
      itemName
    };
    
    // Create detailed notification message
    const itemsList = itemDetails?.items || [];
    const itemCount = itemsList.length;
    const actionDescription = itemName || (itemCount > 0 ? `${itemCount} item(s)` : 'Confirm action');
    
    // Send OTP to notification center with short message
    // Items will be shown in the modal when user clicks "View & Verify"
    console.log('🔐 Sending OTP notification via addNotification...');
    addNotification(
      `🔐 Verification Code: ${newOtp}`,
      actionDescription,
      'warning',
      metadata,
      'notifications.otp' // Require OTP notification permission
    );
    console.log('🔐 OTP notification sent via addNotification');
    
    // Also show a brief toast to let user know
    toast.success('Verification code sent to notifications', {
      duration: 3000,
      icon: '🔔'
    });
  };

  const handleOtpChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only last digit
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = pastedData.split('');
    setOtp([...newOtp, ...Array(6 - newOtp.length).fill('')]);
    
    // Focus the next empty input or last input
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }

    if (otpString !== generatedOtp) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      
      if (newAttempts >= maxAttempts) {
        toast.error('Too many attempts. Please try again.');
        onClose();
        return;
      }
      
      toast.error(`Invalid OTP. ${maxAttempts - newAttempts} ${maxAttempts - newAttempts === 1 ? 'attempt' : 'attempts'} left.`);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      return;
    }

    if (timeLeft <= 0) {
      toast.error('OTP expired. Generating new one...');
      generateOTP();
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      return;
    }

    setIsLoading(true);
    
    try {
      await onConfirm();
      toast.success('Action confirmed successfully!');
      onClose();
    } catch (error) {
      console.error('Error confirming action:', error);
      toast.error('Failed to complete action.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds) => {
    return `${seconds}s`;
  };

  if (!isOpen) return null;

  const otpString = otp.join('');
  const isComplete = otpString.length === 6;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl animate-in zoom-in-95 duration-300">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          disabled={isLoading}
        >
          <X size={20} className="text-gray-400 hover:text-gray-600" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-4">
            <Lock size={28} className="text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Verify Action</h3>
          <p className="text-gray-500 text-sm">
            Enter the 6-digit code to confirm
          </p>
        </div>

        {/* OTP Input Boxes */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-center gap-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={isLoading}
                className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl transition-all duration-200 focus:outline-none ${
                  digit
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-gray-300 bg-white text-gray-800'
                } ${
                  isLoading ? 'opacity-50 cursor-not-allowed' : 'focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                }`}
              />
            ))}
          </div>

          {/* Timer and Status */}
          <div className="flex items-center justify-center gap-4 text-sm">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${
              timeLeft <= 10 ? 'bg-red-50 text-red-600' : 'bg-gray-50 text-gray-600'
            }`}>
              <Clock size={14} />
              <span className="font-medium">{formatTime(timeLeft)}</span>
            </div>
            {attempts > 0 && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-600 rounded-full">
                <span className="font-medium">{maxAttempts - attempts} attempts left</span>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isComplete || isLoading || timeLeft <= 0}
            className={`w-full py-3.5 rounded-xl font-semibold text-white transition-all duration-200 ${
              !isComplete || isLoading || timeLeft <= 0
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Verifying...</span>
              </div>
            ) : (
              'Confirm Action'
            )}
          </button>

          {/* Resend Button */}
          <button
            type="button"
            onClick={() => {
              generateOTP();
              setOtp(['', '', '', '', '', '']);
              setAttempts(0);
              setTimeLeft(25);
              inputRefs.current[0]?.focus();
            }}
            disabled={isLoading}
            className="w-full text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
          >
            Didn't receive code? <span className="text-blue-600 hover:text-blue-700">Resend</span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default OTPConfirmationModal;
